import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

const READY_TIMEOUT_MS = 90000;
const INITIAL_DELAY_MS = 800;
const DOM_SETTLE_MS = 2000;
const STABILITY_BUFFER_MS = 600;
const POLL_MS = 150;

const waitForWindowLoad = (): Promise<void> => {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.addEventListener("load", () => resolve(), { once: true });
  });
};

const waitForFonts = async (): Promise<void> => {
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }
};

const waitFrame = (): Promise<void> =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const isElementVisible = (el: Element): boolean => {
  if (!(el instanceof HTMLElement)) return true;
  if (!el.isConnected) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none") return false;
  if (style.visibility === "hidden") return false;
  if (parseFloat(style.opacity) === 0) return false;
  return el.offsetParent !== null || style.position === "fixed";
};

const hasVisibleLoader = (): boolean => {
  const spinners = document.querySelectorAll(".MuiCircularProgress-root");
  return Array.from(spinners).some(isElementVisible);
};

const allImagesComplete = (): boolean =>
  Array.from(document.images).every((img) => img.complete);

const waitForAllImages = async (): Promise<void> => {
  const pending = Array.from(document.images).filter((img) => !img.complete);
  if (!pending.length) return;
  await Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          const done = () => resolve();
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        })
    )
  );
};

const waitForDomStable = async (
  settleMs: number,
  deadline: number
): Promise<void> => {
  await new Promise<void>((resolve) => {
    let lastMutation = Date.now();
    const observer = new MutationObserver(() => {
      lastMutation = Date.now();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    const tick = () => {
      const now = Date.now();
      if (now - lastMutation >= settleMs || now > deadline) {
        observer.disconnect();
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
};

const waitForReady = async (): Promise<void> => {
  const deadline = Date.now() + READY_TIMEOUT_MS;
  await sleep(INITIAL_DELAY_MS);

  while (Date.now() < deadline) {
    while (hasVisibleLoader() && Date.now() < deadline) {
      await sleep(POLL_MS);
    }

    await waitForDomStable(DOM_SETTLE_MS, deadline);
    await waitForAllImages();

    await sleep(STABILITY_BUFFER_MS);

    if (!hasVisibleLoader() && allImagesComplete()) {
      await waitFrame();
      if (!hasVisibleLoader() && allImagesComplete()) {
        return;
      }
    }
  }
};

const useGlossaryPrint = () => {
  const isManualPrint = useRef(false);
  const [isPrintView, setIsPrintView] = useState(false);

  useEffect(() => {
    const onBeforePrint = () => {
      if (!isManualPrint.current) {
        setIsPrintView(true);
      }
    };
    const onAfterPrint = () => {
      setIsPrintView(false);
      isManualPrint.current = false;
    };

    window.addEventListener("beforeprint", onBeforePrint);
    window.addEventListener("afterprint", onAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", onBeforePrint);
      window.removeEventListener("afterprint", onAfterPrint);
    };
  }, []);

  const print = () => {
    isManualPrint.current = true;
    flushSync(() => setIsPrintView(true));
    window.print();
  };

  const printWhenReady = async () => {
    isManualPrint.current = true;
    flushSync(() => setIsPrintView(true));

    await waitForWindowLoad();
    await waitForFonts();
    await waitForReady();

    window.print();
  };

  return { isPrintView, print, printWhenReady };
};

export default useGlossaryPrint;
